import i18n from 'i18next';
import merge from 'lodash.merge';
import Backend from 'i18next-chained-backend';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import resources from '../locales/translations';
import { getLocale } from '../utils/get-locale-util';
import { getEnvVariable } from '../utils/get-env-variable';
import { logError } from '../utils/logger';

const DEFAULT_LOCALE = 'en';
const DEFAULT_LOCALE_ENGLISH = 'english';

i18n.use(initReactI18next).init({
  fallbackLng: DEFAULT_LOCALE,
  lng: DEFAULT_LOCALE,
  debug: false,
  resources,
  interpolation: {
    escapeValue: false,
  },
  preload: [DEFAULT_LOCALE],
  ns: ['common'],
  defaultNS: 'common',
  supportedLngs: [DEFAULT_LOCALE],
});

export async function initI18n(sessionToken: string) {
  const { customLocale } = getLocale();
  const shouldFetchLocale = !(
    customLocale.toLowerCase() === DEFAULT_LOCALE || customLocale.toLowerCase() === DEFAULT_LOCALE_ENGLISH
  );

  // Perf (C.2): English (the most common locale) is already covered by the
  // statically-bundled `resources`. Skip the whole HTTP-backend setup and
  // bail before doing any async work so the boot waterfall isn't held by an
  // unnecessary `await`.
  if (!shouldFetchLocale) {
    return;
  }

  if (shouldFetchLocale) {
    try {
      const apiBaseUrl = getEnvVariable('VITE_API_URL', 'https://api.appcharge.com');

      await i18n.use(Backend).init({
        ...i18n.options,
        backend: {
          backends: [HttpBackend],
          backendOptions: [
            {
              loadPath: `${apiBaseUrl}/checkout/translations/languages/${customLocale}`,
              requestOptions: {
                headers: {
                  Authorization: `Bearer ${sessionToken}`,
                },
              },
              parse: parseResponse,
            },
          ],
        },
        supportedLngs: [DEFAULT_LOCALE, customLocale],
        lng: customLocale,
        fallbackLng: DEFAULT_LOCALE,
        partialBundledLanguages: true,
      });

      i18n.emit('supportedLanguagesChanged', [customLocale, DEFAULT_LOCALE]);
      await changeLanguage(customLocale);
    } catch (error) {
      logError('Error during i18n initialization', error);
    }
  }
}

async function changeLanguage(language: string) {
  try {
    await i18n.reloadResources(language);
    await i18n.changeLanguage(language);
  } catch (error) {
    logError('Error changing language', error, { language });
    if (language !== DEFAULT_LOCALE) {
      await i18n.changeLanguage(DEFAULT_LOCALE);
    }
  }
}

function parseResponse(stringifiedTranslations: string) {
  try {
    const parsedTranslations = JSON.parse(stringifiedTranslations);
    const mergedTranslations = merge(resources.en.common.checkout, parsedTranslations.checkout);
    return mergedTranslations;
  } catch (error) {
    logError('Error parsing translations response', error);
    return resources.en.common;
  }
}

export default i18n;
